/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {Device} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {PageState} from '@/types/pages';
import {AdminAction} from '@/types/redux';
import {
  GET_ADMIN_DEVICES_ERROR,
  GET_ADMIN_DEVICES_REQUEST,
  GET_ADMIN_DEVICES_RESPONSE,
  INITIALIZE_ADMIN_DEVICES,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-devices';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminDevicesState extends PageState {
  devices: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean
  }
};

export interface AdminDevicesAction extends AdminAction {
  device?: Device;
  devices?: Device[];
};

// defines the user preference key for the admin devices page ...
const PREFERENCE = 'admin-devices';

// defines the initial state for the reducer ...
export const initialState: AdminDevicesState = {
  isInitialized : false,
  devices       : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_DEVICES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, devices}: AdminDevicesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devices.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminDevicesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_DEVICES_REQUEST] : ({devices}: AdminDevicesState) => {
    devices.loading = true;
  },
  [GET_ADMIN_DEVICES_RESPONSE] : ({devices}: AdminDevicesState) => {
    devices.loading = false;
  },
  [GET_ADMIN_DEVICES_ERROR] : ({devices}: AdminDevicesState, {error}: AdminDevicesAction) => {
    devices.loading = false;
    devices.error   = error;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminDevicesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
